import { useState } from 'react';
import { useApi, identityApiRef } from '@mainstage/core-plugin-api';
import { ResponseErrorPanel } from '@mainstage/core-components';
import { WbCard, WbCardContent } from '@agilelab/plugin-wb-platform';
import Grid from '@material-ui/core/Grid';
import LinearProgress from '@material-ui/core/LinearProgress';
import useAsync from 'react-use/lib/useAsync';
import {
  useGetAccessTokens,
  CreateAccessTokenForm,
  AccessTokenTable,
  AccessTokenStatus,
  DeleteAccessTokenDialog,
  RotateAccessTokenDialog,
  AccessToken,
  SafeAccessToken,
} from '@agilelab/backstage-plugin-wb-service-accounts';

export function UserAccessTokenSettings() {
  const identityApi = useApi(identityApiRef);

  // Get current user's entity ref
  const { value: identity, loading: loadingIdentity } = useAsync(
    () => identityApi.getBackstageIdentity(),
    [identityApi],
  );
  const userEntityRef = identity?.userEntityRef;

  // Fetch tokens using the user's entity ref
  const {
    isLoading,
    data: accessTokens,
    error,
  } = useGetAccessTokens({
    accountRef: userEntityRef ?? '',
    enabled: !!userEntityRef,
  });

  // State for dialogs and generated token
  const [tokenToDelete, setTokenToDelete] = useState<SafeAccessToken | null>(
    null,
  );
  const [tokenToRotate, setTokenToRotate] = useState<SafeAccessToken | null>(
    null,
  );
  const [generatedToken, setGeneratedToken] = useState<AccessToken | null>(
    null,
  );

  if (loadingIdentity || isLoading) {
    return (
      <Grid container direction="row" spacing={3}>
        <Grid item xs={12}>
          <WbCard title="Access Token">
            <WbCardContent>
              <LinearProgress />
            </WbCardContent>
          </WbCard>
        </Grid>
      </Grid>
    );
  }

  if (error) {
    return (
      <Grid container direction="row" spacing={3}>
        <Grid item xs={12}>
          <WbCard title="Access Token">
            <WbCardContent>
              <ResponseErrorPanel error={error} />
            </WbCardContent>
          </WbCard>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container direction="row" spacing={3}>
      <Grid item xs={12}>
        <WbCard title="Access Token">
          <WbCardContent
            style={{ display: 'flex', flexDirection: 'column', gap: 24 }}
          >
            <AccessTokenStatus
              accessTokens={accessTokens}
              generatedToken={generatedToken}
              isUser
            />

            {accessTokens?.length ? (
              <AccessTokenTable
                accessTokens={accessTokens}
                onDeleteToken={setTokenToDelete}
                onRotateToken={setTokenToRotate}
              />
            ) : (
              <CreateAccessTokenForm
                entityRef={userEntityRef!}
                onTokenCreated={setGeneratedToken}
              />
            )}

            <DeleteAccessTokenDialog
              token={tokenToDelete}
              onClose={() => setTokenToDelete(null)}
            />
            <RotateAccessTokenDialog
              token={tokenToRotate}
              onClose={() => setTokenToRotate(null)}
              onRotated={setGeneratedToken}
            />
          </WbCardContent>
        </WbCard>
      </Grid>
    </Grid>
  );
}
